/**
 * Card no topo da aba "Direção Técnica" que permite ao Consultor/Admin
 * gerar e enviar por email um link público à DT para revisar e aprovar
 * o Plano de Cuidados sem precisar de fazer login.
 *
 * 27/02/2026 — Pedido do utilizador: "para além da DT receber o pedido
 * para validar o PI por e-mail cria link publico com token".
 */
import { useState } from "react";
import { Loader2, Send, Copy, CheckCircle2, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import DepositProgress from "@/components/DepositProgress";

export default function DTReviewLinkCard({ lead, onSent }) {
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // 09/09/2026 — Fix: o card só reconhecia `assigned_care_plan_dt_*`
  // (campos preenchidos manualmente), mas quando a DT é atribuída via
  // Enquadramento Global (`POST /leads/{id}/dt-assign`) os campos
  // gravados são `assigned_dt_*`. O backend em `care_plan_dt_review.py`
  // já faz fallback para essa segunda fonte — o card só precisa de
  // reconhecer AMBAS as origens para mostrar a DT correcta ao user.
  const dtEmail = lead?.assigned_care_plan_dt_email
                    || lead?.assigned_dt_email;
  const dtName = lead?.assigned_care_plan_dt_name
                    || lead?.assigned_dt_name;
  const hasDT = !!(lead?.assigned_care_plan_dt_id
                    || lead?.assigned_dt_id
                    || dtEmail);
  // Gate DT: 09/09/2026 — Utilizador removeu a exigência de caução
  // TOTALMENTE paga. Basta a caução mínima (kickstart / kickoff) estar
  // paga — alinha este gate com Enquadramento/Recruitment/PIC. O flag
  // ``deposit.kickstart_paid`` do backend é calculado a partir dos
  // pagamentos vs. ``plan.kickstart_amount`` e vem no lead após
  // qualquer PATCH DAF.
  const proposalAccepted = !!(lead?.acceptance?.accepted);
  const deposit = lead?.deposit || {};
  const depositPlanKickstart = Number(deposit?.plan?.kickstart_amount) || 0;
  const depositPaidTotal = (deposit?.payments || []).reduce(
    (s, p) => s + (Number(p?.amount) || 0), 0
  );
  const kickstartPaid = Boolean(deposit?.kickstart_paid)
                          || (depositPlanKickstart > 0
                              && depositPaidTotal + 0.005 >= depositPlanKickstart);
  const gateOk = proposalAccepted && kickstartPaid;

  const sendLink = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/leads/${lead.id}/care-plan/dt-review/send-link`,
        { notes: notes || null },
      );
      setResult(data);
      if (data.email_sent) {
        toast.success(`Link enviado para ${data.sent_to}`);
      } else {
        toast.warning(
          `Link gerado, mas o email falhou (${data.email_error?.slice(0, 100)}). Copia o link e envia manualmente.`,
        );
      }
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a enviar link");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!result?.review_url) return;
    try {
      await navigator.clipboard.writeText(result.review_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="dt-review-link-card">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
        Envio à Direção Técnica · Link público
      </div>
      <h3 className="text-base font-semibold text-foreground mb-1">
        Pedir revisão e aprovação à DT por email
      </h3>
      <p className="text-sm text-muted-foreground mb-4 leading-snug">
        Gera um link com token que a DT abre no browser sem precisar de fazer
        login. O formulário já aparece pré-preenchido com tudo o que o Consultor
        preparou. A DT ajusta horários e tarefas e aprova.
      </p>

      {!hasDT && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
          <div className="text-xs font-semibold text-amber-900">Sem DT atribuída</div>
          <div className="text-xs text-amber-800 mt-1">
            Atribui uma Direção Técnica ao Plano de Cuidados antes de gerar o link.
          </div>
        </div>
      )}

      {hasDT && !gateOk && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4 space-y-3" data-testid="dt-review-link-gate">
          <div>
            <div className="text-xs font-semibold text-amber-900">
              Ainda não é possível gerar o link para a DT
            </div>
            <ul className="text-xs text-amber-800 mt-1 space-y-0.5 list-disc list-inside">
              {!proposalAccepted && (
                <li>Falta a <b>aceitação da proposta</b> pelo cliente.</li>
              )}
              {!kickstartPaid && (
                <li>Falta a <b>caução de arranque (kickoff) estar paga</b>.</li>
              )}
            </ul>
            <div className="text-[11px] text-amber-800/80 mt-2">
              Assim que estas condições estiverem cumpridas, o botão fica ativo.
            </div>
          </div>
          {!kickstartPaid && depositPlanKickstart > 0 && (
            <DepositProgress deposit={deposit} testid="dt-link-deposit-progress" />
          )}
        </div>
      )}

      {hasDT && (
        <div className="rounded-xl border border-border bg-muted px-4 py-3 mb-4">
          <div className="text-xs text-muted-foreground">DT atribuída</div>
          <div className="text-sm font-semibold text-foreground">{dtName || "—"}</div>
          {dtEmail && <div className="text-xs text-muted-foreground">{dtEmail}</div>}
        </div>
      )}

      <label className="flex flex-col gap-1 mb-4">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
          Nota adicional (opcional)
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex: Cliente pediu início já na próxima semana — precisamos de urgência."
          rows={2}
          disabled={sending}
          data-testid="dt-review-link-notes"
          className="text-sm bg-card border border-border rounded-lg px-3 py-2 focus:border-primary focus:outline-none disabled:bg-muted resize-y"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={sendLink}
          disabled={sending || !hasDT || !gateOk}
          data-testid="dt-review-send-link-btn"
          title={!gateOk ? "Aguarda aceitação da proposta + caução de arranque paga" : undefined}
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "A gerar…" : (result ? "Gerar novo link" : "Gerar link e enviar à DT")}
        </button>
        {result?.review_url && (
          <>
            <a
              href={result.review_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-card border border-border text-muted-foreground hover:bg-muted"
              data-testid="dt-review-link-open"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Abrir preview
            </a>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-card border border-border text-muted-foreground hover:bg-muted"
              data-testid="dt-review-link-copy"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copiado" : "Copiar link"}
            </button>
          </>
        )}
      </div>

      {result && (
        <div className="mt-4 rounded-xl border border-border bg-muted p-3 space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <Mail className={result.email_sent ? "w-3.5 h-3.5 text-emerald-600" : "w-3.5 h-3.5 text-amber-600"} />
            <span className="font-semibold text-foreground">
              {result.email_sent ? `Email enviado a ${result.sent_to}` : `Email NÃO enviado`}
            </span>
          </div>
          {result.email_error && (
            <div className="text-[11px] text-amber-800">{result.email_error}</div>
          )}
          <div className="text-[11px] text-muted-foreground">
            Válido até: <b>{result.expires_at?.slice(0, 10)}</b>
          </div>
          <div className="text-[11px] text-muted-foreground font-mono break-all">
            {result.review_url}
          </div>
        </div>
      )}
    </div>
  );
}
